import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  Alert,
  DocumentsCard,
  helpers,
  OrderItemDetailModal,
  returnStatuses,
  ToastContext,
  TransactionDetailHeader,
  useFetch,
} from '@reveni-io/reveni-ui'

import EditAddressModal from '@/common/components/EditAddressModal/EditAddressModal'
import EditCustomsInfoModal from '@/common/components/EditCustomsInfoModal/EditCustomsInfoModal'
import EmailNotificationDrawer from '@/common/components/EmailNotificationDrawer/EmailNotificationDrawer'
import SendEmailDrawer from '@/common/components/SendEmailDrawer/SendEmailDrawer'
import { emailTypes } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import { websocketActionsWorkspaces } from '@/common/context/websocketActions/constants'
import { shouldIncludeRejectedItems } from '@/common/helpers/shipmentDeclaration/helpers'
import useCustomsInformation from '@/common/hooks/useCustomsInformation/useCustomsInformation'
import useEmailNotificationDrawer from '@/common/hooks/useEmailNotificationDrawer/useEmailNotificationDrawer'
import useNavigateBetweenElements from '@/common/hooks/useNavigateBetweenElements/useNavigateBetweenElements'
import useStores from '@/common/hooks/useStores/useStores'
import useWebsocketActions from '@/common/hooks/useWebsocketActions/useWebsocketActions'
import CancelOrderConfirmationModal from '@/returns/components/CancelOrderConfirmationModal/CancelOrderConfirmationModal'
import ChargebackErrorModal from '@/returns/components/ChargebackErrorModal/ChargebackErrorModal'
import IssueExchangeAndRefundConfirmationModal from '@/returns/components/IssueExchangeAndRefundConfirmationModal/IssueExchangeAndRefundConfirmationModal'
import IssueExchangeConfirmationModal from '@/returns/components/IssueExchangeConfirmationModal/IssueExchangeConfirmationModal'
import IssueRefundConfirmationModal from '@/returns/components/IssueRefundConfirmationModal/IssueRefundConfirmationModal'
import { statuses, transactionTypes } from '@/returns/constants'
import { getFromExchangeOrderData } from '@/returns/helpers/helpers'
import useChangeStatus from '@/returns/hooks/useChangeStatus/useChangeStatus'
import useChargebackErrorModal from '@/returns/hooks/useChargebackErrorModal/useChargebackErrorModal'
import returnsServices from '@/returns/services/services'
import routes from '@/Router/routes.constants'

import IssueRefundDrawer from '../TransactionIssueRefundPage/IssueRefundDrawer/IssueRefundDrawer'
import EditTransactionAmountDrawer from '../TransactionValidationPage/EditTransactionAmountDrawer/EditTransactionAmountDrawer'
import AdditionalReturnedProductsCard from './AdditionalReturnedProductsCard/AdditionalReturnedProductsCard'
import AddTrackingNumberModal from './AddTrackingNumberModal/AddTrackingNumberModal'
import AmendRejectedDrawer from './AmendRejectedDrawer/AmendRejectedDrawer'
import AppliedActionsCard from './AppliedActionsCard/AppliedActionsCard'
import CancelGiftCardDrawer from './CancelGiftCardDrawer/CancelGiftCardDrawer'
import CancelStoreCreditDrawer from './CancelStoreCreditDrawer/CancelStoreCreditDrawer'
import ChangeDeliveryMethodDrawer from './ChangeDeliveryMethodDrawer/ChangeDeliveryMethodDrawer'
import ChangeRefundMethodDrawer from './ChangeRefundMethodDrawer/ChangeRefundMethodDrawer'
import CreateExchangeOrderDrawer from './CreateExchangeOrderDrawer/CreateExchangeOrderDrawer'
import CustomerDetailCard from './CustomerDetailCard/CustomerDetailCard'
import DeclineOnHoldDrawer from './DeclineOnHoldDrawer/DeclineOnHoldDrawer'
import EditCustomerDetailModal from './EditCustomerDetailModal/EditCustomerDetailModal'
import FailedCancelStoreCreditModal from './FailedCancelStoreCreditModal/FailedCancelStoreCreditModal'
import useAmendRejected from './hooks/useAmendRejected/useAmendRejected'
import useCancelGiftCard from './hooks/useCancelGiftCard/useCancelGiftCard'
import useCancelStoreCredit from './hooks/useCancelStoreCredit/useCancelStoreCredit'
import useChangeRefundMethod from './hooks/useChangeRefundMethod/useChangeRefundMethod'
import useCreateExchangeOrder from './hooks/useCreateExchangeOrder/useCreateExchangeOrder'
import useDeliveryMethods from './hooks/useDeliveryMethods/useDeliveryMethods'
import useEditCustomer from './hooks/useEditCustomer/useEditCustomer'
import useGenerateLabel from './hooks/useGenerateLabel/useGenerateLabel'
import useIssueRefundDrawer from './hooks/useIssueRefundDrawer/useIssueRefundDrawer'
import useMarkAlertAsResolved from './hooks/useMarkAlertAsResolved/useMarkAlertAsResolved'
import useMarkAsIssued from './hooks/useMarkAsIssued/useMarkAsIssued'
import useMarkAsPaid from './hooks/useMarkAsPaid/useMarkAsPaid'
import useModifyAmounts from './hooks/useModifyAmounts/useModifyAmounts'
import useOnHold from './hooks/useOnHold/useOnHold'
import usePutRefundOnHold from './hooks/usePutRefundOnHold/usePutRefundOnHold'
import useResendPaymentLink from './hooks/useResendPaymentLink/useResendPaymentLink'
import useRevertCancelStatus from './hooks/useRevertCancelStatus/useRevertCancelStatus'
import useSchedulePickup from './hooks/useSchedulePickup/useSchedulePickup'
import useSendEmail from './hooks/useSendEmail/useSendEmail'
import useShippingDetails from './hooks/useShippingDetails/useShippingDetails'
import useShowItem from './hooks/useShowItem/useShowItem'
import useSwitchExchangeToRefund from './hooks/useSwitchExchangeToRefund/useSwitchExchangeToRefund'
import useTransactionPermissions from './hooks/useTransactionPermissions/useTransactionPermissions'
import useTransactionTags from './hooks/useTransactionTags/useTransactionTags'
import { pendingWebsocketActionTypes } from './hooks/useWebsocketTransactionAction/constants'
import useWebsocketTransactionAction from './hooks/useWebsocketTransactionAction/useWebsocketTransactionAction'
import LineItemsToReturnCard from './LineItemsToReturnCard/LineItemsToReturnCard'
import MarkAsIssuedModal from './MarkAsIssuedModal/MarkAsIssuedModal'
import MarkAsPaidModal from './MarkAsPaidModal/MarkAsPaidModal'
import NewLineItemsRequestedCard from './NewLineItemsRequestedCard/NewLineItemsRequestedCard'
import OnHoldCard from './OnHoldCard/OnHoldCard'
import OriginalPurchaseDetailCard from './OriginalPurchaseDetailCard/OriginalPurchaseDetailCard'
import PutRefundOnHoldDrawer from './PutRefundOnHoldDrawer/PutRefundOnHoldDrawer'
import ResendPaymentLinkModal from './ResendPaymentLinkModal/ResendPaymentLinkModal'
import ReturnGroupReminder from './ReturnGroupReminder/ReturnGroupReminder'
import RevertCancelStatusModal from './RevertCancelStatusModal/RevertCancelStatusModal'
import SchedulePickupDrawer from './SchedulePickupDrawer/SchedulePickupDrawer'
import SendInstructionsModal from './SendInstructionsModal/SendInstructionsModal'
import ShippingDetailsCard from './ShippingDetailsCard/ShippingDetailsCard'
import SwitchExchangeToRefundModal from './SwitchExchangeToRefundModal/SwitchExchangeToRefundModal'
import TransactionAlert from './TransactionAlert/TransactionAlert'
import TransactionDetailCard from './TransactionDetailCard/TransactionDetailCard'
import TransactionDetailPageSkeleton from './TransactionDetailPageSkeleton/TransactionDetailPageSkeleton'
import TransactionSummaryCard from './TransactionSummaryCard/TransactionSummaryCard'
import TransactionTimeLineCard from './TransactionTimeLineCard/TransactionTimeLineCard'
import { actionsMenuSections, actionsReasons } from './constants'
import {
  canAmendRejected,
  getCustomerDetails,
  getDocumentsCardDetail,
  getEditTransactionResultConcepts,
  getMoreActionsMenuItems,
  getOriginalCustomerDetails,
  setLineItemActionType,
  sortLineItemsByStatus,
} from './helpers'
import * as Styles from './styles'

const TransactionDetailPage = ({ isUnifiedDashboard }) => {
  const { id } = useParams()
  const location = useLocation()
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { t, i18n } = useTranslation()

  const { data, loading, fetchData } = useFetch(() => returnsServices.getReturnDetail({ returnId: id }), {
    doInitialRequest: true,
  })

  const {
    data: timeline,
    loading: timelineLoading,
    fetchData: fetchTimeline,
  } = useFetch(() => returnsServices.getReturnTimeline({ returnId: id }), {
    doInitialRequest: true,
  })

  const {
    isSummaryCardLoading,
    isDetailCardLoading,
    isShippingDetailsCardLoading,
    isDocumentsCardLoading,
    isLineItemsToReturnCardLoading,
    isFullPageLoading,
  } = useWebsocketTransactionAction({
    fetchData,
    fetchTimeline,
  })
  const { setPendingWebsocketAction } = useWebsocketActions(websocketActionsWorkspaces.transactions)

  const handleChangeStatusSuccess = response => {
    setPendingWebsocketAction(pendingWebsocketActionTypes.changeTransactionStatus, response)
  }

  const { showPaymentLinkModal, openPaymentLinkModal, closePaymentLinkModal, resendPaymentLink, sendingPaymentLink } =
    useResendPaymentLink({
      returnId: id,
    })

  const {
    showSendEmailModal,
    openSendEmailModalForContactCustomer,
    closeSendEmailModal,
    to,
    setTo,
    cc,
    setCc,
    replyTo,
    setReplyTo,
    subject,
    setSubject,
    body,
    setBody,
    onSendEmail,
    loading: sendingEmail,
    copies: sendEmailCopies,
    allowSendEmail,
  } = useSendEmail({
    returnId: data?.id,
    customerEmail: data?.customer_details?.email,
    storeReplyToEmail: info?.selectedStore?.reply_to_email,
    returnStatus: data?.status,
    setToastInfo,
    refetchReturn: fetchData,
  })

  const { showMarkAsIssuedModal, handleMarkAsIssued, markingAsIssued, openMarkAsIssuedModal, closeMarkAsIssuedModal } =
    useMarkAsIssued({
      orderId: data?.id,
      setToastInfo,
    })

  const { showChargebackModal, closeChargebackModal, handleChargebackError } = useChargebackErrorModal()

  const {
    showIssueRefundDrawer,
    openIssueRefundDrawer,
    closeIssueRefundDrawer,
    handleRefundIssued,
    handleIssueRefundError,
  } = useIssueRefundDrawer({ setToastInfo, handleChargebackError })

  const {
    showCancelStoreCreditDrawer,
    cancellingStoreCredit,
    openCancelStoreCreditDrawer,
    closeCancelStoreCreditDrawer,
    handleCancelStoreCredit,
    showFailedCancelStoreCreditModal,
    closeFailedCancelStoreCreditModal,
    notes: storeCreditNotes,
    setNotes: setStoreCreditNotes,
    issueOriginalPaymentMethod: storeCreditIssueOpm,
    setIssueOriginalPaymentMethod: setStoreCreditIssueOpm,
  } = useCancelStoreCredit({
    orderId: data?.id,
    fetchData,
    fetchTimeline,
    setToastInfo,
    handleChargebackError,
  })

  const {
    showCancelGiftCardDrawer,
    cancellingGiftCard,
    openCancelGiftCardDrawer,
    closeCancelGiftCardDrawer,
    handleCancelGiftCard,
    notes: giftCardNotes,
    setNotes: setGiftCardNotes,
    issueOriginalPaymentMethod: giftCardIssueOpm,
    setIssueOriginalPaymentMethod: setGiftCardIssueOpm,
    giftCard,
    fetchingGiftCard,
    giftCardBlockingMessage,
    canVoidGiftCard,
  } = useCancelGiftCard({
    orderId: data?.id,
    fetchData,
    fetchTimeline,
    setToastInfo,
    handleChargebackError,
  })

  const {
    showPutRefundOnHoldDrawer,
    openPutRefundOnHoldDrawer,
    closePutRefundOnHoldDrawer,
    handlePutRefundOnHold,
    puttingRefundOnHold,
    refundHoldMode,
    setRefundHoldMode,
    refundHoldDelayDays,
    setRefundHoldDelayDays,
    refundHoldReason,
    setRefundHoldReason,
  } = usePutRefundOnHold({
    returnId: data?.id,
    fetchData,
    fetchTimeline,
    setToastInfo,
  })

  const {
    showCreateExchangeOrderDrawer,
    openCreateExchangeOrderDrawer,
    closeCreateExchangeOrderDrawer,
    handleCreateExchangeOrder,
    creatingExchangeOrder,
    includeCancelled,
    setIncludeCancelled,
  } = useCreateExchangeOrder({
    returnId: id,
    setToastInfo,
  })

  const { handleMarkAlertAsResolved } = useMarkAlertAsResolved({
    returnId: id,
    fetchData,
    fetchTimeline,
    setToastInfo,
  })

  const { showItemModal, onCloseItemModal, onSelectItem, selectedItem } = useShowItem()

  const {
    showRevertCancelStatusModal,
    openRevertCancelStatusModal,
    closeRevertCancelStatusModal,
    handleRevertCancelStatus,
    revertingCancelStatus,
  } = useRevertCancelStatus({
    orderId: data?.id,
    fetchData,
    fetchTimeline,
    setToastInfo,
  })

  const navigation = useNavigate()
  const {
    allowsEditTransactionTotal,
    allowsEditReturnStatus,
    allowsEditRefundMethod,
    allowsEditReturnLabels,
    isDisabledIssueRefund,
    isAcceptOrRejectDisabled,
    isDisabledForOnHoldAndPendingStatus,
    allowRevertCancelStatus,
    allowPutRefundOnHold,
    allowMarkAsIssued,
    allowMarkAsPaid,
    allowCancelStoreCredit,
    allowCancelGiftCard,
    allowPartialValidation,
    allowCreateExchangeOrder,
    allowEditCustomer,
    allowAddTrackingNumber,
    allowChangeDeliveryMethod,
    allowSendInstructions,
    allowIssueOpmOnCancel,
  } = useTransactionPermissions(data)

  const {
    onChangeStatus,
    handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived,
    handleConfirmCancelOrder,
    showIssueRefundConfirmationModal,
    setShowCancelOrderConfirmationModal,
    setShowIssueExchangeAndRefundConfirmationModal,
    setShowIssueExchangeConfirmationModal,
    setShowIssueRefundConfirmationModal,
    showCancelOrderConfirmationModal,
    showIssueExchangeAndRefundConfirmationModal,
    showIssueExchangeConfirmationModal,
  } = useChangeStatus({ onChangeSuccess: handleChangeStatusSuccess })

  const { onGetMoreElements, onNavigateToElementDetail } = useNavigateBetweenElements({
    currentId: id,
    selectedStoreId: isUnifiedDashboard ? info.selectedStore.id : null,
    fetchDetailElement: fetchData,
    elementDetailRoute: isUnifiedDashboard
      ? routes.merchantTransactionsTransactionDetailPage
      : routes.transactionDetailPage,
    elementsInContext: info.transactions,
    fetchElements: returnsServices.getReturns,
    fetchTimeLine: fetchTimeline,
    filtersInContext: info.filters,
    nameOfElementsInContext: 'transactions',
    nameOfFiltersInContext: 'filters',
    updateContext,
  })

  const {
    onGenerateLabel,
    onRetryLabel,
    generatingLabel,
    isRetryLabelHidden,
    isRetryLabelDisabled,
    retryLabelDisabledReason,
  } = useGenerateLabel({
    returnId: id,
    includesRejectedItems: shouldIncludeRejectedItems(data),
    allowsGenerateLabelReason: data?.permissions?.allows_generate_label_reason,
    pickupScheduled: data?.shipping?.pickup_scheduled,
    isShippingActionInProgress: isShippingDetailsCardLoading,
    timeZone: info?.selectedStore?.timezone,
    setToastInfo,
  })

  const {
    showItemsCustomsInfoModal,
    setShowItemsCustomsInfoModal,
    loadingItemsCustomsInfo,
    lineItemsCustomsInfo,
    loadingEditItemsCustomsInfo,
    onChangeItemCustomsInfo,
    onConfirmItemsCustomsInfo,
    openEditCustomsInformationModal,
    regions,
    loadingRegions,
  } = useCustomsInformation({
    resourceId: id,
    lineItems: data?.line_items || [],
    setToastInfo,
    editCustomsInformationService: returnsServices.editReturnCustomsInfo,
    onGenerateLabel,
    getCustomsInformationService: returnsServices.getReturnItemsCustomsInfo,
    includeRejectedItems: shouldIncludeRejectedItems(data),
  })
  const { deliveryMethods, loading: fetchingDeliveryMethods } = useDeliveryMethods({
    returnId: id,
  })

  const {
    showSwitchExchangeToRefundModal,
    switchExchangeToRefund,
    switchingExchangeToRefund,
    closeSwitchExchangeToRefundModal,
    openSwitchExchangeToRefundModal,
  } = useSwitchExchangeToRefund({
    returnId: id,
    fetchData,
  })

  const {
    showChangeRefundMethodDrawer,
    openChangeRefundMethodDrawer,
    closeChangeRefundMethodDrawer,
    refundMethodSelected: changeRefundMethodSelected,
    setRefundMethodSelected: setChangeRefundMethodSelected,
    handleChangeRefundMethod,
    changingRefundMethod,
  } = useChangeRefundMethod({
    returnId: id,
    currentRefundMethod: data?.refund_info?.refund_method,
    setToastInfo,
  })

  const { showEditCustomerModal, openEditCustomerModal, closeEditCustomerModal, editingCustomer, onEditCustomer } =
    useEditCustomer({
      fetchData,
      fetchTimeline,
      setToastInfo,
      returnId: id,
    })

  const {
    showEditAddressModal,
    openEditAddressModal,
    openEditAddressAndRetryLabelModal,
    closeEditAddressModal,
    retryLabelAfterEditAddress,
    onEditAddress,
    editingAddress,
    showTrackingNumberModal,
    openTrackingNumberModal,
    closeTrackingNumberModal,
    saveTrackingNumber,
    savingTrackingNumber,
    showChangeDeliveryMethodDrawer,
    openChangeDeliveryMethodDrawer,
    closeChangeDeliveryMethodDrawer,
    onChangeDeliveryMethod,
    changingDeliveryMethod,
    sendingInstructions,
    onSendInstructions,
    openSendInstructionsModal,
    closeSendInstructionsModal,
    showSendInstructionsModal,
  } = useShippingDetails({
    data,
    fetchData,
    setToastInfo,
    onRetryLabel,
  })

  const {
    showSchedulePickupDrawer,
    openSchedulePickupDrawer,
    closeSchedulePickupDrawer,
    onSchedulePickup,
    schedulingPickup,
    datesAndTimeRangesForSchedulePickup,
    fetchingDatesAndTimeRangesForSchedulePickup,
    selectedPickupDate,
    setSelectedPickupDate,
    selectedTimeRange,
    setSelectedTimeRange,
    isSchedulePickupDisabled,
  } = useSchedulePickup({
    orderId: data?.id,
    setToastInfo,
    allowsSchedulePickupReason: allowsEditReturnLabels
      ? data?.permissions?.allows_schedule_pickup_reason
      : actionsReasons.notSupported,
  })

  const {
    showAmendRejectedDrawer,
    openAmendRejectedDrawer,
    closeAmendRejectedDrawer,
    restock,
    restockLocation,
    amendingRejected,
    isAmendRejectedDisabled: isAmendRejectedDisabledByStatus,
    rejectedItems,
    handleChangeRestock,
    handleChangeLocation,
    handleAmendRejected,
    locations,
    provider,
    itemSelections,
    setItemSelection,
    amendedCount,
    notifyCustomer,
    handleChangeNotifyCustomer,
  } = useAmendRejected({ transaction: data, fetchData, fetchTimeline })

  const isAmendRejectedDisabled = !canAmendRejected(isAmendRejectedDisabledByStatus(), allowsEditReturnStatus)

  const {
    handleOnAcceptOnHold,
    handleOnDeclineOnHold,
    acceptingOnHold,
    decliningOnHold,
    showDeclineOnHoldDrawer,
    openDeclineOnHoldDrawer,
    closeDeclineOnHoldDrawer,
    declineReason,
    setDeclineReason,
  } = useOnHold({
    returnId: data?.id,
    fetchData,
    fetchTimeline,
    setToastInfo,
  })

  const {
    showEmailNotificationDrawer,
    selectedEmailNotification,
    openEmailNotificationDrawer,
    closeEmailNotificationDrawer,
    emailNotificationPreview,
    fetchingEmailNotificationPreview,
    handleResendEmailNotification,
    resendingEmailNotification,
  } = useEmailNotificationDrawer({
    resourceId: data?.id,
    timeline,
    getPreviewService: returnsServices.getReturnNotificationPreview,
    resendService: returnsServices.resendReturnNotification,
    refetchTimeline: fetchTimeline,
  })

  const {
    showEditTransactionResultDrawer,
    openEditTransactionResultDrawer,
    closeEditTransactionResultDrawer,
    handleSaveModifyAmounts,
    modifying,
  } = useModifyAmounts({
    returnId: data?.id,
    fetchData,
    fetchTimeline,
    setToastInfo,
  })

  const getEditTransactionResultLabel = () => {
    const paymentTypes = [transactionTypes.exchange, transactionTypes.instantExchange, transactionTypes.mixed]
    if (paymentTypes.includes(data?.transaction_type)) {
      return t('Editar pago pendiente')
    }
    return t('Editar reembolso pendiente')
  }

  const { getStoreNameById } = useStores()

  const { showMarkAsPaidModal, handleMarkAsPaid, markingAsPaid, openMarkAsPaidModal, closeMarkAsPaidModal } =
    useMarkAsPaid({
      orderId: data?.id,
      setToastInfo,
    })

  const { tags, handleChange: onChangeTags } = useTransactionTags({
    returnId: id,
    initialTags: data?.tags,
    setToastInfo,
  })

  const sendTrackingNumber = trackingNumber => {
    saveTrackingNumber(
      { returnId: data?.id, trackingNumber },
      () => {
        setToastInfo({ show: true, message: t('Número de seguimiento actualizado'), type: 'success' })
        fetchData()
        fetchTimeline()
      },
      err => {
        setToastInfo({ show: true, message: err.errorMessage, type: 'error' })
      }
    )
  }

  const onGoToPartialValidationPage = () => {
    navigation(routes.transactionValidationPage.replace(':id', id), { state: { from: location } })
  }

  const onReviewAdditionalReturnedProducts = () => {
    navigation(routes.transactionReviewAdditionalProducts.replace(':id', id), { state: { from: location } })
  }

  const handleOnAcceptPartialValidation = () => {
    onChangeStatus({
      orderId: data?.id,
      status: statuses.accepted,
    })
  }

  const handleOnRejectPartialValidation = () => {
    onChangeStatus({
      orderId: data?.id,
      status: statuses.rejected,
    })
  }

  const editTransactionResultConcepts = useMemo(() => {
    return getEditTransactionResultConcepts({
      refundConcepts: data?.refund_info?.refund_concepts,
      lineItems: data?.line_items,
      lineItemGroups: data?.line_item_groups,
    })
  }, [data])

  const onIssueRefund = () => {
    if (!allowsEditTransactionTotal) {
      openIssueRefundDrawer()

      return
    }

    navigation(routes.transactionIssueRefund.replace(':id', data.id), { state: { from: location } })
  }

  const openUrlInNewTab = url => {
    if (url) {
      window.open(url, '_blank')
    }
  }

  const onClickCustomersUrl = () => openUrlInNewTab(data?.customer_details?.customers_url)

  const onClickOrderTrackingPage = () => openUrlInNewTab(data?.order?.order_tracking_page_url)

  const moreActionsMenuSections = actionsMenuSections.map(section => ({
    ...section,
    label: t(section.label),
  }))

  const moreActionsMenuItems = getMoreActionsMenuItems({
    t,
    data,
    allowsEditReturnStatus,
    allowPartialValidation,
    allowCreateExchangeOrder,
    allowMarkAsIssued,
    allowMarkAsPaid,
    allowCancelStoreCredit,
    allowCancelGiftCard,
    allowEditCustomer,
    allowAddTrackingNumber,
    allowChangeDeliveryMethod,
    allowSendInstructions,
    handleOnAcceptPartialValidation,
    handleOnRejectPartialValidation,
    onGoToPartialValidationPage,
    openAmendRejectedDrawer,
    openRevertCancelStatusModal,
    openSwitchExchangeToRefundModal,
    openMarkAsIssuedModal,
    openPaymentLinkModal,
    openCancelStoreCreditDrawer,
    openCancelGiftCardDrawer,
    openSendEmailModalForContactCustomer,
    allowSendEmail,
    openEditCustomerModal,
    onClickCustomersUrl,
    openEditAddressModal,
    openSchedulePickupDrawer,
    isSchedulePickupDisabled,
    openTrackingNumberModal,
    openChangeDeliveryMethodDrawer,
    openChangeRefundMethodDrawer,
    openSendInstructionsModal,
    onIssueRefund,
    isDisabledIssueRefund,
    openPutRefundOnHoldDrawer,
    allowPutRefundOnHold,
    isAcceptOrRejectDisabled,
    isAmendRejectedDisabled,
    allowRevertCancelStatus,
    isDisabledForOnHoldAndPendingStatus,
    openMarkAsPaidModal,
    openCreateExchangeOrderDrawer,
    onReviewAdditionalReturnedProducts,
    onClickOrderTrackingPage,
    orderTrackingPageUrl: data?.order?.order_tracking_page_url,
  })

  const lineItems = sortLineItemsByStatus(
    setLineItemActionType(helpers.joinLineItemsAndLineItemGroups(data?.line_items, data?.line_item_groups)) || []
  )

  const customerDetails = getCustomerDetails(data)

  const documentsCardDetail = getDocumentsCardDetail(data)

  if (loading || fetchingDeliveryMethods || isFullPageLoading) return <TransactionDetailPageSkeleton />

  return (
    <Styles.Container>
      <TransactionDetailHeader
        availableStatus={allowsEditReturnStatus ? data?.available_status : []}
        orderNumber={data?.order?.name}
        status={data?.status}
        transactionType={data?.transaction_type}
        isInstant={data?.is_instant}
        isCoveredByReturnProtection={data?.is_covered_by_return_protection}
        onAccept={handleOnAcceptPartialValidation}
        onReject={handleOnRejectPartialValidation}
        currentTransactionId={data?.id}
        transactions={info?.transactions}
        onNavigateToTransactionDetail={onNavigateToElementDetail}
        onGetMoreTransactions={onGetMoreElements}
        allowList={data?.allow_list}
        fromExchangeOrder={getFromExchangeOrderData(data?.order?.from_exchange_order)}
        sequencePosition={data?.sequence_number}
        sequenceTotal={data?.returns_in_order}
        moreActionsMenuSections={moreActionsMenuSections}
        moreActionsMenuItems={moreActionsMenuItems}
        defaultBackUrl={isUnifiedDashboard ? routes.merchantTransactionsPage : routes.transactionsPage}
        storeName={getStoreNameById(data?.store_id)}
        isGiftReturn={data?.gift?.is_gift_return}
      />
      <OnHoldCard
        status={data?.status}
        onHoldReason={data?.on_hold_reason_display}
        onHoldTime={data?.on_hold_at}
        onAccept={handleOnAcceptOnHold}
        onDecline={openDeclineOnHoldDrawer}
        acceptingOnHold={acceptingOnHold}
        decliningOnHold={decliningOnHold}
        allowsEditReturnStatus={allowsEditReturnStatus}
        allowsEditReturnLabels={allowsEditReturnLabels}
        onChangeDeliveryMethod={openChangeDeliveryMethodDrawer}
        isChangeDeliveryMethodDisabled={!data?.permissions?.allows_update_address}
        onEditTransactionResult={openEditTransactionResultDrawer}
        editTransactionResultLabel={getEditTransactionResultLabel()}
      />
      {data?.alerts?.map(alert => (
        <TransactionAlert
          key={alert.id}
          alert={alert}
          onClickMissingCustomsData={openEditCustomsInformationModal}
          onClickIssueRefund={onIssueRefund}
          onClickCreateExchangeOrder={openCreateExchangeOrderDrawer}
          onClickMarkAsResolved={handleMarkAlertAsResolved}
          onClickContactCustomer={openSendEmailModalForContactCustomer}
          onClickReviewAdditionalReturnedProducts={onReviewAdditionalReturnedProducts}
          onClickEditAddressAndRetryLabel={openEditAddressAndRetryLabelModal}
          onClickRetryLabel={onRetryLabel}
          isRetryLabelHidden={isRetryLabelHidden}
          isRetryLabelDisabled={isRetryLabelDisabled}
          retryLabelDisabledReason={retryLabelDisabledReason}
          storeId={data?.store_id}
        />
      ))}
      <Styles.MainContainer>
        <Styles.SectionContainer>
          <LineItemsToReturnCard
            loading={isLineItemsToReturnCardLoading}
            items={lineItems}
            onSelectItem={onSelectItem}
            showPartialValidationButton={allowPartialValidation}
            onGoToPartialValidationPage={onGoToPartialValidationPage}
            eta={data?.status === returnStatuses.shipped ? data?.eta : null}
          />
          <AdditionalReturnedProductsCard
            unexpectedItems={data?.unexpected_items}
            onReviewAdditionalReturnedProducts={onReviewAdditionalReturnedProducts}
            allowsEditReturnStatus={allowsEditReturnStatus}
          />
          <NewLineItemsRequestedCard items={lineItems} newItems={data?.new_items || []} />
          <TransactionSummaryCard
            loading={isSummaryCardLoading}
            data={data}
            onIssueRefund={onIssueRefund}
            onCancelStoreCredit={openCancelStoreCreditDrawer}
            onCancelGiftCard={openCancelGiftCardDrawer}
            disabledIssueRefund={isDisabledIssueRefund}
            onPutRefundOnHold={openPutRefundOnHoldDrawer}
            allowPutRefundOnHold={allowPutRefundOnHold}
            allowMarkAsIssued={allowMarkAsIssued}
            allowCancelStoreCredit={allowCancelStoreCredit}
            allowCancelGiftCard={allowCancelGiftCard}
            onMarkAsIssued={openMarkAsIssuedModal}
            onMarkAsPaid={openMarkAsPaidModal}
            allowMarkAsPaid={allowMarkAsPaid}
          />
          <TransactionTimeLineCard
            data={data}
            fetchTimeline={fetchTimeline}
            timeline={timeline}
            timelineLoading={timelineLoading}
            onEmailNotificationClick={openEmailNotificationDrawer}
          />
        </Styles.SectionContainer>
        <Styles.SectionContainer>
          <TransactionDetailCard
            loading={isDetailCardLoading}
            data={data}
            onChangeStatus={onChangeStatus}
            onIssueRefund={onIssueRefund}
            onCancelStoreCredit={openCancelStoreCreditDrawer}
            onCancelGiftCard={openCancelGiftCardDrawer}
            disabledIssueRefund={isDisabledIssueRefund}
            onPutRefundOnHold={openPutRefundOnHoldDrawer}
            allowPutRefundOnHold={allowPutRefundOnHold}
            allowsResendPaymentLink={data?.permissions?.allows_resend_payment_link}
            onResendPaymentLink={openPaymentLinkModal}
            allowRevertCancelStatus={allowRevertCancelStatus}
            disabledRevertCancelStatus={!allowsEditReturnStatus}
            onRevertCancelStatus={openRevertCancelStatusModal}
            customersUrl={data?.customer_details?.customers_url}
            onClickCustomersUrl={onClickCustomersUrl}
            onSwitchExchangeToRefund={openSwitchExchangeToRefundModal}
            allowSwitchExchangeToRefund={data?.permissions?.allows_switch_to_refund}
            onMarkAsIssued={openMarkAsIssuedModal}
            allowMarkAsIssued={allowMarkAsIssued}
            allowCancelStoreCredit={allowCancelStoreCredit}
            allowCancelGiftCard={allowCancelGiftCard}
            onAmendRejected={openAmendRejectedDrawer}
            disabledAmendRejected={isAmendRejectedDisabled}
            onMarkAsPaid={openMarkAsPaidModal}
            allowMarkAsPaid={allowMarkAsPaid}
            tags={tags}
            onChangeTags={onChangeTags}
          />
          <CustomerDetailCard
            name={customerDetails.name}
            email={customerDetails.email}
            phone={customerDetails.phone}
            address={data?.address}
            onEditCustomer={openEditCustomerModal}
            onContactCustomer={openSendEmailModalForContactCustomer}
            allowSendEmail={allowSendEmail}
            showCustomerMenu={data?.status !== statuses.cancelled}
            storeId={data?.store_id}
            isGiftReturn={data?.gift?.is_gift_return}
            allowsUpdateCustomer={data?.permissions?.allows_update_customer}
          />
          <OriginalPurchaseDetailCard
            orderDate={data?.order?.date}
            externalUrl={data?.order?.external_url}
            orderNumber={data?.order?.name || `#${data?.order?.number}`}
            fromExchangeOrder={getFromExchangeOrderData(data?.order?.from_exchange_order)}
            customer={getOriginalCustomerDetails(data)}
          />
          {data?.shipping?.delivery_method && (
            <ShippingDetailsCard
              data={data}
              sendTrackingNumber={sendTrackingNumber}
              fetchData={fetchData}
              fetchTimeline={fetchTimeline}
              savingTrackingNumber={savingTrackingNumber}
              onSchedulePickup={openSchedulePickupDrawer}
              isSchedulePickupDisabled={isSchedulePickupDisabled}
              onEditAddress={openEditAddressModal}
              onAddTrackingNumber={openTrackingNumberModal}
              onSendInstructions={openSendInstructionsModal}
              onChangeDeliveryMethod={openChangeDeliveryMethodDrawer}
              onGenerateLabel={onGenerateLabel}
              generatingLabel={generatingLabel}
              showShippingMenu={data?.status !== statuses.cancelled}
              isDisabledForOnHoldAndPendingStatus={isDisabledForOnHoldAndPendingStatus}
              loading={isShippingDetailsCardLoading}
            />
          )}
          <AppliedActionsCard
            allowList={data?.allow_list}
            policyRules={data?.policy_rules}
            isCoveredByReturnProtection={data?.is_covered_by_return_protection}
          />
          <DocumentsCard
            loading={isDocumentsCardLoading}
            showBorder={false}
            transactionDetail={documentsCardDetail}
            returnGroupInfoComponent={<ReturnGroupReminder returnGroup={data?.return_group} currentId={data?.id} />}
            timeZone={info?.selectedStore?.timezone}
          />
        </Styles.SectionContainer>
      </Styles.MainContainer>
      <OrderItemDetailModal show={showItemModal} item={selectedItem} onClose={onCloseItemModal} />
      <IssueRefundConfirmationModal
        show={showIssueRefundConfirmationModal}
        onClose={() => setShowIssueRefundConfirmationModal(false)}
        onConfirm={handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived}
      />
      <IssueExchangeConfirmationModal
        show={showIssueExchangeConfirmationModal}
        onClose={() => setShowIssueExchangeConfirmationModal(false)}
        onConfirm={handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived}
      />
      <IssueExchangeAndRefundConfirmationModal
        show={showIssueExchangeAndRefundConfirmationModal}
        onClose={() => setShowIssueExchangeAndRefundConfirmationModal(false)}
        onConfirm={handleConfirmIssueRefundOrExchangeWhenChangeStatusToReceived}
      />
      <CancelOrderConfirmationModal
        show={showCancelOrderConfirmationModal}
        onClose={() => setShowCancelOrderConfirmationModal(false)}
        onConfirm={handleConfirmCancelOrder}
      />
      <CancelStoreCreditDrawer
        show={showCancelStoreCreditDrawer}
        onClose={closeCancelStoreCreditDrawer}
        cancellingStoreCredit={cancellingStoreCredit}
        onCancelStoreCredit={handleCancelStoreCredit}
        allowIssueOpmOnCancel={allowIssueOpmOnCancel}
        notes={storeCreditNotes}
        setNotes={setStoreCreditNotes}
        issueOriginalPaymentMethod={storeCreditIssueOpm}
        setIssueOriginalPaymentMethod={setStoreCreditIssueOpm}
      />
      <CancelGiftCardDrawer
        show={showCancelGiftCardDrawer}
        onClose={closeCancelGiftCardDrawer}
        cancellingGiftCard={cancellingGiftCard}
        onCancelGiftCard={handleCancelGiftCard}
        allowIssueOpmOnCancel={allowIssueOpmOnCancel}
        notes={giftCardNotes}
        setNotes={setGiftCardNotes}
        issueOriginalPaymentMethod={giftCardIssueOpm}
        setIssueOriginalPaymentMethod={setGiftCardIssueOpm}
        giftCard={giftCard}
        fetchingGiftCard={fetchingGiftCard}
        giftCardBlockingMessage={giftCardBlockingMessage}
        canVoidGiftCard={canVoidGiftCard}
      />
      <PutRefundOnHoldDrawer
        show={showPutRefundOnHoldDrawer}
        onClose={closePutRefundOnHoldDrawer}
        onPutRefundOnHold={handlePutRefundOnHold}
        puttingRefundOnHold={puttingRefundOnHold}
        mode={refundHoldMode}
        setMode={setRefundHoldMode}
        delayDays={refundHoldDelayDays}
        setDelayDays={setRefundHoldDelayDays}
        reason={refundHoldReason}
        setReason={setRefundHoldReason}
      />
      <DeclineOnHoldDrawer
        show={showDeclineOnHoldDrawer}
        onClose={closeDeclineOnHoldDrawer}
        onDeclineRequest={handleOnDeclineOnHold}
        declining={decliningOnHold}
        declineReason={declineReason}
        setDeclineReason={setDeclineReason}
      />
      <FailedCancelStoreCreditModal
        show={showFailedCancelStoreCreditModal}
        onClose={closeFailedCancelStoreCreditModal}
      />
      <ChargebackErrorModal show={showChargebackModal} onClose={closeChargebackModal} />
      <ResendPaymentLinkModal
        show={showPaymentLinkModal}
        onClose={closePaymentLinkModal}
        onResendPaymentLink={resendPaymentLink}
        loading={sendingPaymentLink}
      />
      <RevertCancelStatusModal
        show={showRevertCancelStatusModal}
        onClose={closeRevertCancelStatusModal}
        onRevertCancelStatus={handleRevertCancelStatus}
        revertingCancelStatus={revertingCancelStatus}
        hasReturnLabels={data?.shipping?.return_labels?.length > 0}
      />
      <EditCustomsInfoModal
        show={showItemsCustomsInfoModal}
        items={lineItemsCustomsInfo}
        regions={regions || []}
        loading={loadingItemsCustomsInfo || loadingRegions}
        onChange={onChangeItemCustomsInfo}
        onConfirm={onConfirmItemsCustomsInfo}
        generatingLabel={generatingLabel}
        savingChanges={loadingEditItemsCustomsInfo}
        onClose={() => setShowItemsCustomsInfoModal(false)}
      />
      <SwitchExchangeToRefundModal
        show={showSwitchExchangeToRefundModal}
        onClose={closeSwitchExchangeToRefundModal}
        onConfirm={switchExchangeToRefund}
        loading={switchingExchangeToRefund}
      />
      <MarkAsIssuedModal
        show={showMarkAsIssuedModal}
        onClose={closeMarkAsIssuedModal}
        onMarkAsIssued={handleMarkAsIssued}
        markingAsIssued={markingAsIssued}
        language={i18n.language}
        currency={data?.summary?.currency}
        amount={data?.summary?.summary_total_amount}
      />
      <AmendRejectedDrawer
        show={showAmendRejectedDrawer}
        onClose={closeAmendRejectedDrawer}
        onAmendRejected={handleAmendRejected}
        amendingRejected={amendingRejected}
        rejectedItems={rejectedItems}
        itemSelections={itemSelections}
        onSelectItem={setItemSelection}
        amendedCount={amendedCount}
        notifyCustomer={notifyCustomer}
        onChangeNotifyCustomer={handleChangeNotifyCustomer}
        allowManageRestock={data?.permissions?.allows_manage_restock}
        restockValue={restock}
        onChangeRestock={handleChangeRestock}
        locations={locations}
        selectedLocation={restockLocation}
        onChangeLocation={handleChangeLocation}
        provider={provider}
        storeId={data?.store_id}
      />
      <EditCustomerDetailModal
        show={showEditCustomerModal}
        onClose={closeEditCustomerModal}
        customerDetail={{
          name: customerDetails?.name,
          email: customerDetails?.email,
          phone: customerDetails?.phone,
          language: data?.customer_details?.customer_profile?.language,
        }}
        onEditCustomer={onEditCustomer}
        loading={editingCustomer}
        supportedLanguages={info?.selectedStore.supported_languages}
      />
      <EditAddressModal
        show={showEditAddressModal}
        onClose={closeEditAddressModal}
        onEditAddress={onEditAddress}
        loading={editingAddress}
        shippingAddress={helpers.formatUserAddress(data?.address, data?.customer_details?.phone)}
        deliveryMethod={data?.shipping?.delivery_method}
        warningComponent={
          <Alert type="warning">
            {t('Al editar esta dirección, el sistema intentará generar de nuevo la etiqueta de devolución.')}
          </Alert>
        }
        showWarningComponent={retryLabelAfterEditAddress}
      />
      <SchedulePickupDrawer
        show={showSchedulePickupDrawer}
        onClose={closeSchedulePickupDrawer}
        onSchedule={onSchedulePickup}
        loading={schedulingPickup}
        datesAndTimeRangesForSchedulePickup={datesAndTimeRangesForSchedulePickup}
        fetchingDatesAndTimeRangesForSchedulePickup={fetchingDatesAndTimeRangesForSchedulePickup}
        selectedPickupDate={selectedPickupDate}
        setSelectedPickupDate={setSelectedPickupDate}
        selectedTimeRange={selectedTimeRange}
        setSelectedTimeRange={setSelectedTimeRange}
      />
      <AddTrackingNumberModal
        show={showTrackingNumberModal}
        onClose={closeTrackingNumberModal}
        onAddTrackingNumber={sendTrackingNumber}
        loading={savingTrackingNumber}
      />
      <ChangeDeliveryMethodDrawer
        show={showChangeDeliveryMethodDrawer}
        onClose={closeChangeDeliveryMethodDrawer}
        availableDeliveryMethods={deliveryMethods}
        currentDeliveryMethod={data?.shipping?.delivery_method}
        address={data?.address}
        changeDeliveryMethod={onChangeDeliveryMethod}
        loading={changingDeliveryMethod}
        status={data?.status}
      />
      <ChangeRefundMethodDrawer
        show={showChangeRefundMethodDrawer}
        onClose={closeChangeRefundMethodDrawer}
        refundMethods={data?.refund_info?.refund_methods || []}
        refundMethodSelected={changeRefundMethodSelected}
        currentRefundMethod={data?.refund_info?.refund_method}
        onChangeRefundMethod={setChangeRefundMethodSelected}
        onSave={handleChangeRefundMethod}
        loading={changingRefundMethod}
        allowsEditRefundMethod={allowsEditRefundMethod}
      />
      <EmailNotificationDrawer
        show={showEmailNotificationDrawer}
        onClose={closeEmailNotificationDrawer}
        name={selectedEmailNotification?.properties?.name}
        toName={selectedEmailNotification?.properties?.to_name}
        to={selectedEmailNotification?.properties?.to}
        created={selectedEmailNotification?.created}
        subject={selectedEmailNotification?.properties?.subject}
        attachments={selectedEmailNotification?.properties?.attachments}
        preview={emailNotificationPreview}
        fetchingPreview={fetchingEmailNotificationPreview}
        onResend={handleResendEmailNotification}
        resending={resendingEmailNotification}
        allowsResend={selectedEmailNotification?.properties?.allows_resend}
      />
      <SendInstructionsModal
        show={showSendInstructionsModal}
        onClose={closeSendInstructionsModal}
        steps={data?.shipping?.delivery_method?.steps}
        returnLabels={data?.shipping?.return_labels}
        invoices={[data?.invoice_url, data?.exchanged_for_order?.invoice_url]}
        onSendInstructions={onSendInstructions}
        sendingInstructions={sendingInstructions}
        numReturnLabels={data?.shipping?.delivery_method?.num_return_labels || 0}
      />
      <MarkAsPaidModal
        show={showMarkAsPaidModal}
        onClose={closeMarkAsPaidModal}
        onMarkAsPaid={handleMarkAsPaid}
        markingAsPaid={markingAsPaid}
        language={i18n.language}
        currency={data?.summary?.currency}
        amount={data?.summary?.summary_total_amount}
      />
      <CreateExchangeOrderDrawer
        show={showCreateExchangeOrderDrawer}
        onClose={closeCreateExchangeOrderDrawer}
        onConfirm={handleCreateExchangeOrder}
        loading={creatingExchangeOrder}
        items={lineItems}
        newItems={data?.new_items || []}
        pendingAmount={data?.summary?.pending_amount}
        currency={data?.summary?.currency}
        includeCancelled={includeCancelled}
        setIncludeCancelled={setIncludeCancelled}
      />
      <EditTransactionAmountDrawer
        show={showEditTransactionResultDrawer}
        concepts={editTransactionResultConcepts}
        lineItems={data?.line_items || []}
        refundMethods={data?.refund_info?.refund_methods || []}
        refundMethod={data?.refund_info?.refund_method}
        currency={data?.summary?.currency}
        editable={allowsEditTransactionTotal}
        onClose={closeEditTransactionResultDrawer}
        onSave={handleSaveModifyAmounts}
        returnId={data?.id}
        loading={modifying}
      />
      <IssueRefundDrawer
        show={showIssueRefundDrawer}
        transaction={data}
        returnId={data?.id}
        onClose={closeIssueRefundDrawer}
        onIssued={handleRefundIssued}
        onError={handleIssueRefundError}
        allowsEditRefundMethod={allowsEditRefundMethod}
      />
      <SendEmailDrawer
        show={showSendEmailModal}
        onClose={closeSendEmailModal}
        emailType={emailTypes.returns}
        copies={sendEmailCopies}
        to={to}
        setTo={setTo}
        cc={cc}
        setCc={setCc}
        replyTo={replyTo}
        setReplyTo={setReplyTo}
        subject={subject}
        setSubject={setSubject}
        body={body}
        setBody={setBody}
        onSendEmail={onSendEmail}
        loading={sendingEmail}
        merchantId={info?.id}
      />
    </Styles.Container>
  )
}

export default TransactionDetailPage
